/**
 * Custom Program Management
 * CRUD operations for user-created workout programs
 */

import { db } from './firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { WorkoutProgram, ProgramMetadata, WorkoutDay } from './types/program';

export interface CustomProgramMetadata extends ProgramMetadata {
  isCustom: true;
  createdBy: string;
  isPublic: false; // For future sharing feature
  tags?: string[];
  submittedForReview?: boolean;
  submittedAt?: Date;
  reviewStatus?: 'pending' | 'approved' | 'rejected';
}

export interface CustomProgram extends WorkoutProgram {
  metadata: CustomProgramMetadata;
}

/**
 * Save a custom program to Firestore
 */
/**
 * Remove undefined fields from an object (Firestore doesn't accept undefined)
 */
function sanitizeForFirestore<T extends Record<string, any>>(obj: T): T {
  const cleaned: any = {};
  for (const key in obj) {
    const value = obj[key];
    if (value === undefined) continue;
    
    if (Array.isArray(value)) {
      cleaned[key] = value.map((item: any) => 
        typeof item === 'object' && item !== null ? sanitizeForFirestore(item) : item
      );
    } else if (value !== null && typeof value === 'object' && Object.prototype.toString.call(value) !== '[object Date]') {
      cleaned[key] = sanitizeForFirestore(value);
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned as T;
}

export async function saveCustomProgram(
  userId: string,
  program: Omit<CustomProgram, 'metadata'> & {
    metadata: Omit<CustomProgramMetadata, 'id' | 'createdAt' | 'updatedAt' | 'isCustom' | 'createdBy'>;
  }
): Promise<string> {
  try {
    // Generate unique ID
    const programId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const fullMetadata: CustomProgramMetadata = {
      ...program.metadata,
      id: programId,
      isCustom: true,
      createdBy: userId,
      isPublic: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    const customProgram: CustomProgram = {
      metadata: fullMetadata,
      workouts: program.workouts,
    };

    // Sanitize to remove undefined fields (Firestore doesn't accept them)
    const sanitized = sanitizeForFirestore(customProgram);

    // Save to customPrograms/{userId}/programs/{programId}
    const programRef = doc(db, 'customPrograms', userId, 'programs', programId);
    await setDoc(programRef, sanitized);

    console.log(' Custom program saved:', programId);
    return programId;
  } catch (error) {
    console.error('Error saving custom program:', error);
    throw error;
  }
}

/**
 * Get all custom programs for a user
 */
export async function getUserCustomPrograms(userId: string): Promise<CustomProgram[]> {
  try {
    const programsRef = collection(db, 'customPrograms', userId, 'programs');
    const snapshot = await getDocs(programsRef);

    const programs: CustomProgram[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      programs.push({
        metadata: {
          ...data.metadata,
          createdAt: data.metadata.createdAt?.toDate?.() || data.metadata.createdAt,
          updatedAt: data.metadata.updatedAt?.toDate?.() || data.metadata.updatedAt,
        },
        workouts: data.workouts,
      });
    });

    // Sort by most recently updated
    programs.sort((a, b) => {
      const dateA = a.metadata.updatedAt instanceof Date ? a.metadata.updatedAt : new Date(a.metadata.updatedAt);
      const dateB = b.metadata.updatedAt instanceof Date ? b.metadata.updatedAt : new Date(b.metadata.updatedAt);
      return dateB.getTime() - dateA.getTime();
    });

    return programs;
  } catch (error) {
    console.error('Error loading custom programs:', error);
    return [];
  }
}

/**
 * Get a specific custom program
 */
export async function getCustomProgramById(
  userId: string,
  programId: string
): Promise<CustomProgram | null> {
  try {
    const programRef = doc(db, 'customPrograms', userId, 'programs', programId);
    const programDoc = await getDoc(programRef);

    if (!programDoc.exists()) {
      return null;
    }

    const data = programDoc.data();
    return {
      metadata: {
        ...data.metadata,
        createdAt: data.metadata.createdAt?.toDate?.() || data.metadata.createdAt,
        updatedAt: data.metadata.updatedAt?.toDate?.() || data.metadata.updatedAt,
      },
      workouts: data.workouts,
    };
  } catch (error) {
    console.error('Error loading custom program:', error);
    return null;
  }
}

/**
 * Update an existing custom program
 */
export async function updateCustomProgram(
  userId: string,
  programId: string,
  updates: Partial<CustomProgram>
): Promise<void> {
  try {
    const programRef = doc(db, 'customPrograms', userId, 'programs', programId);

    // Sanitize updates to remove undefined fields
    const sanitized = sanitizeForFirestore({
      ...updates,
      metadata: {
        ...updates.metadata,
        updatedAt: new Date(),
      },
    });

    await updateDoc(programRef, sanitized);
    console.log(' Custom program updated:', programId);
  } catch (error) {
    console.error('Error updating custom program:', error);
    throw error;
  }
}

/**
 * Submit a program for review/public approval
 */
export async function submitProgramForReview(
  userId: string,
  programId: string,
  submissionNote?: string
): Promise<void> {
  try {
    const program = await getCustomProgramById(userId, programId);
    if (!program) {
      throw new Error('Program not found');
    }

    // Create a submission record in programSubmissions collection
    const submissionRef = doc(collection(db, 'programSubmissions'));
    const sanitized = sanitizeForFirestore({
      programId,
      userId,
      submittedAt: new Date(),
      status: 'pending', // pending, approved, rejected
      program: program,
      submissionNote: submissionNote || '',
      reviewedAt: null,
      reviewedBy: null,
      reviewNotes: null,
    });

    await setDoc(submissionRef, sanitized);

    // Update program metadata to mark as submitted
    await updateCustomProgram(userId, programId, {
      metadata: {
        ...program.metadata,
        submittedForReview: true,
        submittedAt: new Date(),
      },
    });

    console.log(' Program submitted for review:', programId);
  } catch (error) {
    console.error('Error submitting program for review:', error);
    throw error;
  }
}

/**
 * Delete a custom program
 */
export async function deleteCustomProgram(userId: string, programId: string): Promise<void> {
  try {
    const programRef = doc(db, 'customPrograms', userId, 'programs', programId);
    await deleteDoc(programRef);
    console.log(' Custom program deleted:', programId);
  } catch (error) {
    console.error('Error deleting custom program:', error);
    throw error;
  }
}

/**
 * Duplicate a custom program
 */
export async function duplicateCustomProgram(
  userId: string,
  programId: string
): Promise<string> {
  try {
    const original = await getCustomProgramById(userId, programId);
    if (!original) {
      throw new Error('Program not found');
    }

    // Create new program with modified metadata
    const newProgramId = await saveCustomProgram(userId, {
      metadata: {
        ...original.metadata,
        name: `${original.metadata.name} (Copy)`,
      },
      workouts: original.workouts,
    });

    return newProgramId;
  } catch (error) {
    console.error('Error duplicating custom program:', error);
    throw error;
  }
}

/**
 * Check if a program is custom (helper function)
 */
export function isCustomProgram(programId: string): boolean {
  return programId.startsWith('custom-');
}

/**
 * Get the appropriate program (official or custom)
 */
export async function getProgramByIdUnified(
  userId: string,
  programId: string
): Promise<WorkoutProgram | null> {
  if (isCustomProgram(programId)) {
    return await getCustomProgramById(userId, programId);
  } else {
    // Load from official programs collection
    const { getProgramById } = await import('./programManagement');
    return await getProgramById(programId);
  }
}

